/**
 * Shared machinery of the motion commands (W1-10b core-commands-motion; DESIGN.md §6, ARCHITECTURE.md §5.4).
 *
 * Every key command works on a plain, mutable copy of a track's keys (`KeyRec[]`): it reads the track, edits the
 * array (insert, remove, move, change values and eases) and writes it back with `commitKeys`, which
 *  - refuses keys that would share a beat ("keysCollide");
 *  - re-establishes the per-track model invariants (DESIGN.md §6.2, core/model/types.ts `Key`):
 *      first key without a jump and not a provenance gap, HOLD ARRIVES AS A JUMP (after a Stay segment the next
 *      key's arrive value is the stayed value), no trailing jump on GIF progress tracks;
 *  - keeps the lead-in invariant (DESIGN.md §6.6): a track of an 'extrapolate' property whose first key would land
 *    after beat 0 gets a Stay key at beat 0 with `Defaults.leadInValue` (or the first value when there is no
 *    exact neutral value), unless it is an imported lead-in; `importedLeadIn` is cleared once a key sits at beat 0;
 *  - writes only what changed (records keep their ids; `tx.update` applies the shadow rules), inserts new keys,
 *    removes keys that are gone, and deletes the track when no key is left (variables fall back to a constant).
 *
 * Failure reasons are MessageRefs in namespace 'cmdMotion' (English text in motion.messages.ts).
 */

import { Defaults } from '../model/defaults';
import { isTrackValueOf, trackValueEquals } from '../model/invariants';
import { hasGifTexture, isUiMover, kindOfLine } from '../model/lineKind';
import { PROP_SPECS, requiresLeadInKey } from '../model/props';
import { stringTweenDetailed } from '../ease/stringTween';
import { HOLD } from '../ease/index';
import type { Ease, EaseUse } from '../ease/types';
import { valueEquals } from '../ops/equal';
import type { RecordPatch, TxBuilder } from '../ops/types';
import { Beats } from '../time/beat';
import type { Beat } from '../time/types';
import type { MessageRef } from '../util/types';
import type {
  ChartDoc,
  DocIndexes,
  EffectVar,
  Key,
  KeyId,
  KeySrc,
  Line,
  LineKind,
  LineProp,
  Track,
  TrackId,
  TrackOwner,
  TrackValue,
  TrackValueType,
} from '../model/types';

// ---------------------------------------------------------------------------------------------------------------
// Failure reasons and notes (namespace 'cmdMotion'; English text in motion.messages.ts)
// ---------------------------------------------------------------------------------------------------------------

export const MOTION_MSG = {
  trackMissing: 'cmdMotion.trackMissing',
  keyMissing: 'cmdMotion.keyMissing',
  lineMissing: 'cmdMotion.lineMissing',
  noKeys: 'cmdMotion.noKeys',
  noTrack: 'cmdMotion.noTrack',
  nothingAnimated: 'cmdMotion.nothingAnimated',
  keysCollide: 'cmdMotion.keysCollide',
  beforeZero: 'cmdMotion.beforeZero',
  valueType: 'cmdMotion.valueType',
  valueNotFinite: 'cmdMotion.valueNotFinite',
  layerOnExtended: 'cmdMotion.layerOnExtended',
  layerInvalid: 'cmdMotion.layerInvalid',
  layerLimit: 'cmdMotion.layerLimit',
  notLayered: 'cmdMotion.notLayered',
  gifNeedsGifTexture: 'cmdMotion.gifNeedsGifTexture',
  varMissing: 'cmdMotion.varMissing',
  firstKeyDelete: 'cmdMotion.firstKeyDelete',
  firstKeyJump: 'cmdMotion.firstKeyJump',
  gifTrailingJump: 'cmdMotion.gifTrailingJump',
  noSegments: 'cmdMotion.noSegments',
  easeSkipped: 'cmdMotion.easeSkipped',
  arriveAfterHold: 'cmdMotion.arriveAfterHold',
  jumpKeptAfterHold: 'cmdMotion.jumpKeptAfterHold',
  splitOutside: 'cmdMotion.splitOutside',
  splitApprox: 'cmdMotion.splitApprox',
  splitApproxText: 'cmdMotion.splitApproxText',
  splitApproxColor: 'cmdMotion.splitApproxColor',
  splitBaked: 'cmdMotion.splitBaked',
  splitBakedText: 'cmdMotion.splitBakedText',
  splitLeadIn: 'cmdMotion.splitLeadIn',
  splitApproxGif: 'cmdMotion.splitApproxGif',
  splitLinearHalf: 'cmdMotion.splitLinearHalf',
  splitPartner: 'cmdMotion.splitPartner',
  bakeDivInvalid: 'cmdMotion.bakeDivInvalid',
  bakeTooMany: 'cmdMotion.bakeTooMany',
  joinNotExact: 'cmdMotion.joinNotExact',
  joinNeedsMiddle: 'cmdMotion.joinNeedsMiddle',
  joinPartner: 'cmdMotion.joinPartner',
  extrapolateNeedsTwo: 'cmdMotion.extrapolateNeedsTwo',
  extrapolateSkipped: 'cmdMotion.extrapolateSkipped',
  linkNeedsTwo: 'cmdMotion.linkNeedsTwo',
  linkPrimary: 'cmdMotion.linkPrimary',
  linkValueType: 'cmdMotion.linkValueType',
  exprNoNumbers: 'cmdMotion.exprNoNumbers',
  exprSkipped: 'cmdMotion.exprSkipped',
  leadInAlready: 'cmdMotion.leadInAlready',
  replaceEmpty: 'cmdMotion.replaceEmpty',
  replaceOrder: 'cmdMotion.replaceOrder',
  replaceValue: 'cmdMotion.replaceValue',
  replaceEase: 'cmdMotion.replaceEase',
  moveNotLayered: 'cmdMotion.moveNotLayered',
  nothingToMove: 'cmdMotion.nothingToMove',
  negateNothing: 'cmdMotion.negateNothing',
  negateSkipped: 'cmdMotion.negateSkipped',
  opacityNothing: 'cmdMotion.opacityNothing',
  opacitySkipped: 'cmdMotion.opacitySkipped',
  timeScaleInvalid: 'cmdMotion.timeScaleInvalid',
  transformCollide: 'cmdMotion.transformCollide',
  transformNothing: 'cmdMotion.transformNothing',
  timeLengthInvalid: 'cmdMotion.timeLengthInvalid',
  timeRangeInvalid: 'cmdMotion.timeRangeInvalid',
  timeLeadIn: 'cmdMotion.timeLeadIn',
  timeShrunk: 'cmdMotion.timeShrunk',
  reverseNothing: 'cmdMotion.reverseNothing',
  pasteNothing: 'cmdMotion.pasteNothing',
  pasteRepeatInvalid: 'cmdMotion.pasteRepeatInvalid',
  pasteUnmatched: 'cmdMotion.pasteUnmatched',
  pasteSkipped: 'cmdMotion.pasteSkipped',
  pasteNotesOnUiMover: 'cmdMotion.pasteNotesOnUiMover',
  pasteHitsoundsDropped: 'cmdMotion.pasteHitsoundsDropped',
  pasteLinesNothing: 'cmdMotion.pasteLinesNothing',
  pasteLinesMissingAssets: 'cmdMotion.pasteLinesMissingAssets',
  noFreeLayer: 'cmdMotion.noFreeLayer',
} as const;

export type MotionMsgKey = (typeof MOTION_MSG)[keyof typeof MOTION_MSG];

export function reason(key: MotionMsgKey, params?: Readonly<Record<string, string | number>>): MessageRef {
  return params === undefined ? { key } : { key, params };
}

// ---------------------------------------------------------------------------------------------------------------
// Views and track context
// ---------------------------------------------------------------------------------------------------------------

/** What a pure read needs (TxBuilder and DocView satisfy it). */
export interface MotionView {
  readonly doc: ChartDoc;
  readonly idx: DocIndexes;
}

/** A track with everything the rules depend on. */
export interface TrackInfo {
  readonly track: Track;
  /** Line property, or null for effect / video variable tracks. */
  readonly prop: LineProp | null;
  readonly line: Line | null;
  readonly layer: number;
  readonly kind: LineKind | null;
  readonly uiMover: boolean;
  readonly valueType: TrackValueType;
  /** Effect / video variable (extra.json keyframe rules). */
  readonly extra: boolean;
}

export function infoOf(view: MotionView, track: Track): TrackInfo {
  const o = track.owner;
  if (o.kind === 'line') {
    const line = view.doc.lines.get(o.line) ?? null;
    return {
      track,
      prop: o.prop,
      line,
      layer: o.layer,
      kind: line === null ? null : kindOfLine(view.doc, view.idx, line),
      uiMover: line !== null && isUiMover(line),
      valueType: track.valueType,
      extra: false,
    };
  }
  return {
    track,
    prop: null,
    line: null,
    layer: 0,
    kind: null,
    uiMover: false,
    valueType: track.valueType,
    extra: true,
  };
}

export function trackInfo(view: MotionView, id: TrackId): TrackInfo | null {
  const t = view.doc.tracks.get(id);
  return t === undefined ? null : infoOf(view, t);
}

/** Placeholder id of a track that does not exist (yet); see `ownerInfo`. */
const PENDING_TRACK = -1 as TrackId;

/**
 * The info of the track of a line property: the existing track, or (`exists` false) the rules of the track that
 * `createTrack` would create, without creating it, so a command can evaluate and split curves for it first (an
 * absent layer is a constant 0). Null when the line does not exist.
 */
export function ownerInfo(
  view: MotionView,
  owner: Extract<TrackOwner, { kind: 'line' }>,
): { readonly info: TrackInfo; readonly exists: boolean } | null {
  const id = view.idx.trackOfOwner(owner);
  const existing = id === undefined ? undefined : view.doc.tracks.get(id);
  if (existing !== undefined) return { info: infoOf(view, existing), exists: true };
  if (!view.doc.lines.has(owner.line)) return null;
  const pending: Track = { id: PENDING_TRACK, owner, valueType: PROP_SPECS[owner.prop].valueType };
  return { info: infoOf(view, pending), exists: false };
}

export function requireTrack(tx: TxBuilder, id: TrackId): TrackInfo {
  return trackInfo(tx, id) ?? tx.fail(reason(MOTION_MSG.trackMissing));
}

export function requireLine(tx: TxBuilder, id: Line['id']): Line {
  return tx.doc.lines.get(id) ?? tx.fail(reason(MOTION_MSG.lineMissing));
}

// ---------------------------------------------------------------------------------------------------------------
// Key records
// ---------------------------------------------------------------------------------------------------------------

/** Mutable working copy of a key; `id` null = a key the command creates. */
export interface KeyRec {
  id: KeyId | null;
  t: Beat;
  vIn: TrackValue;
  vOut: TrackValue;
  ease: Ease;
  link: number;
  src: KeySrc | undefined;
}

export function recOf(k: Key): KeyRec {
  return { id: k.id, t: k.t, vIn: k.vIn, vOut: k.vOut, ease: k.ease, link: k.link, src: k.src };
}

export function newRec(t: Beat, vIn: TrackValue, vOut: TrackValue, ease: Ease, link = 0): KeyRec {
  return { id: null, t, vIn, vOut, ease, link, src: undefined };
}

/** The keys of a track in time order, as working copies. */
export function readKeys(view: MotionView, track: TrackId): KeyRec[] {
  const out: KeyRec[] = [];
  for (const id of view.idx.keysByTrack(track)) {
    const k = view.doc.keys.get(id);
    if (k !== undefined) out.push(recOf(k));
  }
  return out;
}

/** Index of the key at exactly `t`, or -1. */
export function indexAt(keys: readonly KeyRec[], t: Beat): number {
  let lo = 0;
  let hi = keys.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const k = keys[mid] as KeyRec;
    const c = Beats.cmp(k.t, t);
    if (c === 0) return mid;
    if (c < 0) lo = mid + 1;
    else hi = mid - 1;
  }
  return -1;
}

/** Index of the last key with t <= `t` (-1 when `t` is before the first key). */
export function indexAtOrBefore(keys: readonly KeyRec[], t: Beat): number {
  let lo = 0;
  let hi = keys.length - 1;
  let best = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (Beats.lte((keys[mid] as KeyRec).t, t)) {
      best = mid;
      lo = mid + 1;
    } else hi = mid - 1;
  }
  return best;
}

export function indexOfId(keys: readonly KeyRec[], id: KeyId): number {
  return keys.findIndex((k) => k.id === id);
}

/**
 * Writes the arrive value of keys[j] as the end value of the segment arriving there. After the LAST key the curve
 * holds that key's leave value, so a last key that arrived without a jump keeps doing so (its leave value follows);
 * otherwise the value would snap back to the old end right after the segment. Any other key keeps its leave value
 * (the next segment still starts from it: a jump appears there).
 */
export function setArrive(keys: KeyRec[], j: number, v: TrackValue): void {
  const k = keys[j];
  if (k === undefined) return;
  const continuous = sameValue(k.vIn, k.vOut);
  k.vIn = v;
  if (j === keys.length - 1 && continuous) k.vOut = v;
}

// ---------------------------------------------------------------------------------------------------------------
// Values
// ---------------------------------------------------------------------------------------------------------------

/** Removes float noise (0.1 + 0.2 -> 0.3) far below f32 precision, and -0. */
export function tidy(x: number): number {
  if (!Number.isFinite(x)) return x;
  const r = Number(x.toPrecision(12));
  return r === 0 ? 0 : r;
}

export function sameValue(a: TrackValue, b: TrackValue): boolean {
  return trackValueEquals(a, b);
}

export function isValueOf(type: TrackValueType, v: unknown): v is TrackValue {
  return isTrackValueOf(type, v);
}

function clampU8(x: number): number {
  return Math.min(255, Math.max(0, Math.round(x)));
}

/**
 * a + b for the values of line properties: numbers, colours per channel (clamped and rounded); null for text
 * (and for the tuple types of effect variables, which the clipboard never carries).
 */
export function addValue(a: TrackValue, b: TrackValue, type: TrackValueType): TrackValue | null {
  if (type === 'number') return typeof a === 'number' && typeof b === 'number' ? tidy(a + b) : null;
  if (type !== 'rgb') return null;
  const x = a as readonly number[];
  const y = b as readonly number[];
  return [clampU8((x[0] ?? 0) + (y[0] ?? 0)), clampU8((x[1] ?? 0) + (y[1] ?? 0)), clampU8((x[2] ?? 0) + (y[2] ?? 0))];
}

/** -v for numbers (no -0). */
export function negateNumber(v: TrackValue): TrackValue {
  return typeof v === 'number' ? tidy(-v) : v;
}

/** Value written when a property track is created without a value (what an absent track looks like, or close). */
export function neutralValue(info: Pick<TrackInfo, 'prop' | 'layer' | 'kind' | 'uiMover' | 'valueType'>): TrackValue {
  if (info.prop !== null) {
    const lead = Defaults.leadInValue(info.prop, info.layer, info.kind ?? 'normal', info.uiMover);
    if (lead !== null) return lead;
    switch (info.prop) {
      case 'color':
        return [255, 255, 255];
      case 'text':
        return '';
      case 'gif':
        return 0;
      default:
        return 1;
    }
  }
  switch (info.valueType) {
    case 'number':
      return 0;
    case 'vec2':
      return [0, 0];
    case 'rgb':
      return [255, 255, 255];
    case 'rgba8':
      return [255, 255, 255, 255];
    case 'string':
      return '';
  }
}

/** Fails unless `v` is a valid value for the track. */
export function requireValue(tx: TxBuilder, info: TrackInfo, v: unknown): TrackValue {
  if (typeof v === 'number' && !Number.isFinite(v)) tx.fail(reason(MOTION_MSG.valueNotFinite));
  if (!isValueOf(info.valueType, v)) tx.fail(reason(MOTION_MSG.valueType, { type: info.valueType }));
  return typeof v === 'number' ? tidy(v) : v;
}

// ---------------------------------------------------------------------------------------------------------------
// Ease use per segment
// ---------------------------------------------------------------------------------------------------------------

/** The EaseUse guard of a segment (DESIGN.md §4.12, §6.8, §6.11). */
export function easeUseFor(view: MotionView, info: TrackInfo, start: TrackValue, end: TrackValue): EaseUse {
  if (info.extra) return 'extraVar';
  if (info.prop === 'speed') return view.doc.pkg.speedMode === 'rpe170' ? 'speed170' : 'speedLegacy';
  if (info.prop === 'text' && typeof start === 'string' && typeof end === 'string') {
    const c = stringTweenDetailed(start, end, 0.5).case;
    return c === 'reveal' || c === 'erase' ? 'textRevealErase' : 'textOther';
  }
  return 'general';
}

// ---------------------------------------------------------------------------------------------------------------
// Invariants and lead-in
// ---------------------------------------------------------------------------------------------------------------

/**
 * Per-track rules, in place: holds arrive as jumps (k_{i+1}.vIn := k_i.vOut after a Stay), the first key has no
 * jump and is not a gap, a GIF progress track never ends with a jump.
 */
export function enforceKeyRules(keys: KeyRec[], info: Pick<TrackInfo, 'prop'>): void {
  const first = keys[0];
  if (first === undefined) return;
  first.vIn = first.vOut;
  if (first.ease.kind === 'hold' && first.ease.gap === true) first.ease = HOLD;
  for (let i = 1; i < keys.length; i++) {
    const prev = keys[i - 1] as KeyRec;
    const k = keys[i] as KeyRec;
    if (prev.ease.kind === 'hold' && !sameValue(k.vIn, prev.vOut)) k.vIn = prev.vOut;
  }
  const last = keys[keys.length - 1] as KeyRec;
  if (info.prop === 'gif' && !sameValue(last.vIn, last.vOut)) last.vOut = last.vIn;
}

/** Value of the beat-0 Stay key the lead-in invariant inserts (null: hold the first value). */
export function leadInValueOf(info: TrackInfo): number | null {
  if (info.prop === null) return null;
  return Defaults.leadInValue(info.prop, info.layer, info.kind ?? 'normal', info.uiMover);
}

/** True when the track's property needs its first key at beat 0 (lead-in invariant). */
export function needsLeadIn(info: TrackInfo): boolean {
  return info.prop !== null && requiresLeadInKey(info.prop);
}

/**
 * The lead-in Stay key for a track whose first key is after beat 0, or null when none is needed. `keys` is the
 * track as it will be written.
 */
export function leadInKeyFor(info: TrackInfo, importedLeadIn: boolean, keys: readonly KeyRec[]): KeyRec | null {
  const first = keys[0];
  if (first === undefined || importedLeadIn || !needsLeadIn(info)) return null;
  if (Beats.cmp(first.t, Beats.ZERO) <= 0) return null;
  const lead = leadInValueOf(info);
  const v: TrackValue = lead ?? first.vOut;
  return newRec(Beats.ZERO, v, v, HOLD);
}

// ---------------------------------------------------------------------------------------------------------------
// Writing a track
// ---------------------------------------------------------------------------------------------------------------

export interface CommitOptions {
  /** Insert the beat-0 Stay key when the first key would be later (default true). */
  readonly leadIn?: boolean;
}

/**
 * Writes `keys` (time-ordered) as the complete key list of the track; see the file header. `keys` is updated in
 * place with the ids of created keys. Returns false when the track was deleted (no key left).
 */
export function commitKeys(tx: TxBuilder, info: TrackInfo, keys: KeyRec[], opts: CommitOptions = {}): boolean {
  for (let i = 1; i < keys.length; i++) {
    if (!Beats.lt((keys[i - 1] as KeyRec).t, (keys[i] as KeyRec).t)) tx.fail(reason(MOTION_MSG.keysCollide));
  }
  const trackId = info.track.id;
  const track = tx.doc.tracks.get(trackId);
  if (track === undefined) tx.fail(reason(MOTION_MSG.trackMissing));
  if (keys.length === 0) {
    deleteTrack(tx, info);
    return false;
  }
  let imported = track.importedLeadIn === true;
  const first = keys[0] as KeyRec;
  if (imported && Beats.cmp(first.t, Beats.ZERO) <= 0) imported = false;
  if (opts.leadIn ?? true) {
    const lead = leadInKeyFor(info, imported, keys);
    if (lead !== null) keys.unshift(lead);
  }
  if (needsLeadIn(info) && info.line?.origin === 'editor' && Beats.cmp((keys[0] as KeyRec).t, Beats.ZERO) < 0) {
    tx.fail(reason(MOTION_MSG.beforeZero));
  }
  enforceKeyRules(keys, info);
  if ((track.importedLeadIn === true) !== imported) {
    tx.update('tracks', trackId, { importedLeadIn: imported ? true : undefined });
  }
  // Ids that are not (or no longer) keys of this track are written as new keys.
  for (const k of keys) {
    if (k.id !== null && tx.doc.keys.get(k.id)?.track !== trackId) k.id = null;
  }
  const keep = new Set<KeyId>();
  for (const k of keys) if (k.id !== null) keep.add(k.id);
  for (const id of [...tx.idx.keysByTrack(trackId)]) if (!keep.has(id)) tx.remove('keys', id);
  for (const k of keys) {
    if (k.id === null) continue;
    const cur = tx.doc.keys.get(k.id);
    if (cur === undefined) continue;
    const patch = keyPatch(cur, k);
    if (patch !== null) tx.update('keys', k.id, patch);
  }
  for (const k of keys) {
    if (k.id !== null) continue;
    const id = tx.newId('keys');
    k.id = id;
    tx.insert('keys', keyRecord(k, id, trackId));
  }
  return true;
}

function keyRecord(k: KeyRec, id: KeyId, track: TrackId): Key {
  const rec: Key = { id, track, t: k.t, vIn: k.vIn, vOut: k.vOut, ease: k.ease, link: k.link };
  return k.src === undefined ? rec : { ...rec, src: k.src };
}

function keyPatch(cur: Key, k: KeyRec): RecordPatch<Key> | null {
  const patch: { -readonly [P in keyof Key]?: Key[P] | undefined } = {};
  let changed = false;
  if (!Beats.eq(cur.t, k.t)) {
    patch.t = k.t;
    changed = true;
  }
  if (!valueEquals(cur.vIn, k.vIn)) {
    patch.vIn = k.vIn;
    changed = true;
  }
  if (!valueEquals(cur.vOut, k.vOut)) {
    patch.vOut = k.vOut;
    changed = true;
  }
  if (!valueEquals(cur.ease, k.ease)) {
    patch.ease = k.ease;
    changed = true;
  }
  if (cur.link !== k.link) {
    patch.link = k.link;
    changed = true;
  }
  if (!valueEquals(cur.src, k.src)) {
    patch.src = k.src;
    changed = true;
  }
  return changed ? patch : null;
}

/**
 * Deletes a track and its keys. A variable track turns its variable into a constant (its first value), so the
 * effect / video keeps a valid value.
 */
export function deleteTrack(tx: TxBuilder, info: TrackInfo): void {
  const id = info.track.id;
  const ids = [...tx.idx.keysByTrack(id)];
  const firstKey = ids[0] === undefined ? undefined : tx.doc.keys.get(ids[0]);
  for (const k of ids) tx.remove('keys', k);
  const o = info.track.owner;
  if (o.kind === 'effectVar') {
    const e = tx.doc.effects.get(o.effect);
    if (e !== undefined) {
      const vars = e.vars.map((v): EffectVar => {
        if (v.name !== o.name || v.value.kind !== 'track' || v.value.track !== id) return v;
        const c = firstKey?.vOut;
        if (c === undefined || typeof c === 'string' || (Array.isArray(c) && c.length === 3))
          return { ...v, value: { kind: 'null' } };
        return {
          ...v,
          value: {
            kind: 'const',
            v: c as number | readonly [number, number] | readonly [number, number, number, number],
          },
        };
      });
      tx.update('effects', o.effect, { vars });
    }
  } else if (o.kind === 'videoVar') {
    const vid = tx.doc.videos.get(o.video);
    if (vid !== undefined) {
      const c = firstKey?.vOut;
      const value = typeof c === 'number' ? ({ kind: 'const', v: c } as const) : ({ kind: 'default' } as const);
      tx.update('videos', o.video, o.which === 'alpha' ? { alpha: value } : { dim: value });
    }
  }
  tx.remove('tracks', id);
}

// ---------------------------------------------------------------------------------------------------------------
// Creating a track
// ---------------------------------------------------------------------------------------------------------------

/** Value type of a track for an owner (null when the owner does not exist). */
export function valueTypeOf(doc: ChartDoc, owner: TrackOwner): TrackValueType | null {
  switch (owner.kind) {
    case 'line':
      return PROP_SPECS[owner.prop].valueType;
    case 'effectVar': {
      const v = doc.effects.get(owner.effect)?.vars.find((x) => x.name === owner.name);
      if (v === undefined) return null;
      return v.type === 'float' ? 'number' : v.type === 'vec2' ? 'vec2' : 'rgba8';
    }
    case 'videoVar':
      return doc.videos.has(owner.video) ? 'number' : null;
  }
}

/**
 * Creates an EMPTY track record for an owner (the caller writes its keys with `commitKeys` in the same
 * transaction) and points variables at it. Fails when the owner does not exist or cannot have the track.
 */
export function createTrack(tx: TxBuilder, owner: TrackOwner): TrackInfo {
  const valueType = valueTypeOf(tx.doc, owner);
  if (owner.kind === 'line') {
    const line = requireLine(tx, owner.line);
    const spec = PROP_SPECS[owner.prop];
    if (!Number.isSafeInteger(owner.layer) || owner.layer < 0) tx.fail(reason(MOTION_MSG.layerInvalid));
    if (!spec.layered && owner.layer !== 0) tx.fail(reason(MOTION_MSG.layerOnExtended));
    if (owner.prop === 'gif') {
      if (!hasGifTexture(tx.doc, line)) tx.fail(reason(MOTION_MSG.gifNeedsGifTexture));
      if (line.gifAutoLoop) tx.update('lines', line.id, { gifAutoLoop: false });
    }
  }
  if (valueType === null) tx.fail(reason(MOTION_MSG.varMissing));
  const id = tx.newId('tracks');
  const track: Track = { id, owner, valueType };
  tx.insert('tracks', track);
  if (owner.kind === 'effectVar') {
    const e = tx.doc.effects.get(owner.effect);
    if (e !== undefined) {
      const vars = e.vars.map((v): EffectVar =>
        v.name === owner.name ? { ...v, value: { kind: 'track', track: id } } : v,
      );
      tx.update('effects', owner.effect, { vars });
    }
  } else if (owner.kind === 'videoVar') {
    const value = { kind: 'track', track: id } as const;
    tx.update('videos', owner.video, owner.which === 'alpha' ? { alpha: value } : { dim: value });
  }
  return infoOf(tx, track);
}

// ---------------------------------------------------------------------------------------------------------------
// Grouping ids by track
// ---------------------------------------------------------------------------------------------------------------

/** Existing keys grouped by track (unknown ids dropped), tracks in first-seen order. */
export function groupByTrack(view: MotionView, ids: readonly KeyId[]): Map<TrackId, KeyId[]> {
  const out = new Map<TrackId, KeyId[]>();
  const seen = new Set<KeyId>();
  for (const id of ids) {
    if (seen.has(id)) continue;
    seen.add(id);
    const k = view.doc.keys.get(id);
    if (k === undefined) continue;
    const list = out.get(k.track);
    if (list === undefined) out.set(k.track, [id]);
    else list.push(id);
  }
  return out;
}

/**
 * Beats where the tempo changes (BPM points whose tempo differs from the one before), ascending. Segment progress
 * is in seconds, so these are where a segment's easing stops being proportional to beats.
 */
export function tempoChangeBeats(view: MotionView): Beat[] {
  const out: Beat[] = [];
  let prev: number | null = null;
  for (const id of view.idx.bpmSorted()) {
    const p = view.doc.bpm.get(id);
    if (p === undefined) continue;
    if (prev !== null && p.bpm !== prev) out.push(p.t);
    prev = p.bpm;
  }
  return out;
}

/** Earliest beat among the keys (for undo focus), or null. */
export function earliestBeat(view: MotionView, ids: Iterable<KeyId>): Beat | null {
  let best: Beat | null = null;
  for (const id of ids) {
    const k = view.doc.keys.get(id);
    if (k !== undefined && (best === null || Beats.lt(k.t, best))) best = k.t;
  }
  return best;
}
